import { IconMinus, IconTrendingDown, IconTrendingUp } from '@tabler/icons-react';
import { useTranslation } from '../../i18n/LocaleProvider';
import type { DsTranslationKey } from '../../i18n/catalogues/en';
import { motion, useReducedMotion } from 'motion/react';
import type { ComponentPropsWithRef, ReactNode } from 'react';

import { formatMoney, formatNumber, formatPercentage, type LocaleInput } from '../../i18n/format';
import { DUR, EASE } from '../../interaction';
import { cn } from '../../lib/cn';

export type MetricFormat = 'money' | 'percentage' | 'number';
export type MetricTone =
  | 'default'
  | 'primary'
  | 'accent'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'neutral';
export type MetricTrend = 'up' | 'down' | 'flat';

/**
 * `tone` reaches the ICON through a token utility — never a raw colour, and
 * never the value.
 *
 * It used to colour the display number as well, which meant a KPI row with a
 * different tone per card rendered four numbers in four hues: the accent stopped
 * being an accent and became the loudest thing on the row. Measured across the
 * library, that habit is why 140 semantic-token uses sat against 58 copper ones.
 * The number is the thing the row is about and it is set in `--text-heading`;
 * the tone survives on the glyph beside the label, where a hue is a whisper
 * rather than a shout.
 */
const TONES: Record<MetricTone, string> = {
  default: 'text-muted',
  primary: 'text-primary',
  accent: 'text-accent',
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger',
  info: 'text-info',
  neutral: 'text-muted',
};

/**
 * Direction is carried by the glyph AND the screen-reader word; the tone is
 * decoration on top. A red/green pair alone is unreadable to a red-green
 * colour-blind reader, so colour is never the signal.
 */
const TREND_TONES: Record<MetricTrend, string> = {
  up: 'text-success',
  down: 'text-danger',
  flat: 'text-muted',
};

/** Keys, not words: the trend is announced, and it is announced in the reader's
 *  language. Resolved where the metric renders. */
const TREND_WORDS: Record<MetricTrend, DsTranslationKey> = {
  up: 'ds.metric.trend.up',
  down: 'ds.metric.trend.down',
  flat: 'ds.metric.trend.flat',
};

const TREND_GLYPHS: Record<MetricTrend, ReactNode> = {
  up: <IconTrendingUp aria-hidden="true" className="size-3.5 shrink-0" stroke={2.5} />,
  down: <IconTrendingDown aria-hidden="true" className="size-3.5 shrink-0" stroke={2.5} />,
  flat: <IconMinus aria-hidden="true" className="size-3.5 shrink-0" stroke={2.5} />,
};

/**
 * Loading is a state, not decoration: the bars pulse whenever they are on
 * screen, and the global `prefers-reduced-motion` rule in `global.css` settles
 * them into a static block while `aria-busy` stays on the root.
 */
const SHIMMER_BAR = 'animate-pulse rounded-xs bg-surface-muted';

/** The one place a formatter is picked. Every value is formatted, never templated. */
function formatValue(
  value: number,
  format: MetricFormat,
  precision: number | undefined,
  locale: LocaleInput,
): string {
  const options = { locale, ...(precision === undefined ? {} : { precision }) };
  if (format === 'money') return formatMoney(value, options);
  if (format === 'percentage') return formatPercentage(value, options);
  return formatNumber(value, options);
}

export interface MetricProps extends ComponentPropsWithRef<'div'> {
  /** Small uppercase caption above the number. */
  label: string;
  value?: number | null;
  format?: MetricFormat;
  /** Fraction digits handed to the locale formatter. */
  precision?: number;
  prefix?: string;
  suffix?: string;
  locale?: LocaleInput;
  trend?: MetricTrend;
  /** Signed change, formatted through the same formatters as `value`. */
  delta?: number;
  /** Defaults to `format`. */
  deltaFormat?: MetricFormat;
  /**
   * Unit for the delta, rendered after the number.
   *
   * A delta is a difference between two measurements and does not always carry
   * the same unit as the value it sits under: 68% minus 40.33% is 27.67
   * percentage *points*, and printing it with `format="percentage"` reads as a
   * relative change. There is no `pp` in `src/i18n/format.ts` and adding one
   * there would put a unit into a module that formats numbers, so the unit is
   * supplied by the caller that knows it.
   */
  deltaSuffix?: string;
  deltaLabel?: string;
  icon?: ReactNode;
  tone?: MetricTone;
  loading?: boolean;
  hint?: string;
}

/**
 * A KPI databadge: caption, formatted number, and an optional delta line whose
 * direction is announced by an arrow and a screen-reader word rather than by
 * colour.
 *
 * The root is a plain `div` so a consumer's `ref`, `id`, `style` and `data-*`
 * land on a predictable element; the entrance motion lives on an inner
 * `motion.div`, because a motion root retypes `onDrag`/`onAnimation*` away from
 * their native handlers.
 */
export function Metric({
  label,
  value,
  format = 'number',
  precision,
  prefix,
  suffix,
  locale,
  trend,
  delta,
  deltaFormat,
  deltaSuffix,
  deltaLabel,
  icon,
  tone = 'default',
  loading = false,
  hint,
  className,
  ...rest
}: MetricProps) {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();

  const finiteValue = typeof value === 'number' && Number.isFinite(value) ? value : null;
  const finiteDelta = typeof delta === 'number' && Number.isFinite(delta) ? delta : null;

  const valueText =
    finiteValue === null ? '—' : formatValue(finiteValue, format, precision, locale);
  const deltaText =
    finiteDelta === null
      ? null
      : formatValue(finiteDelta, deltaFormat ?? format, precision, locale);
  const hasDeltaLine = trend !== undefined || deltaText !== null || Boolean(deltaLabel);

  const badge = (
    <>
      <div className="flex items-start justify-between gap-2">
        <span className="font-display text-2xs font-semibold uppercase tracking-label text-muted">
          {label}
        </span>
        {icon ? (
          <span
            aria-hidden="true"
            className={cn('grid size-4 shrink-0 place-items-center', TONES[tone])}
          >
            {icon}
          </span>
        ) : null}
      </div>

      {/* No `tabular-nums` here, and that is a measured decision, not a
          stylistic one. `--font-display` is Oswald, which ships no `tnum`
          feature: at 72px, "1111111" / "8888888" / "0000000" are 190.52 /
          251.50 / 260.58px under both `font-variant-numeric: normal` and
          `tabular-nums` — byte-identical, so the declaration did nothing and
          the digits spread 27.2→37.2px (37%). Inter does honour it (326.81px
          for all three), so setting this value in `--font-body` was tried and
          measured first — and reverted: Inter's digits are 33% wider, and at a
          1280px viewport the four-column KPI grid gives a 188px card in which
          "$ 2,510,000" wrapped to two lines (line box 86px, card 212px, against
          the `SkeletonKpiCards` reservation of 135px). Wrapping the headline
          number is a worse defect than a digit that does not align, and the
          zero-margin fix does not exist: `65ch` of card is not enough for both.
          The honest resolution is to stop claiming the feature on a face that
          does not have it — see the note beside `--font-display` in
          `tokens.css`. */}
      <p className="mt-2 font-display text-3xl font-semibold text-heading">
        {prefix ? <span className="mr-1 text-xl">{prefix}</span> : null}
        {valueText}
        {suffix ? <span className="ml-1 text-lg text-muted">{suffix}</span> : null}
      </p>

      {hasDeltaLine ? (
        <div className="mt-1.5 flex min-h-4 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs">
          {trend !== undefined ? (
            <span className={cn('inline-flex items-center gap-1 font-semibold', TREND_TONES[trend])}>
              {TREND_GLYPHS[trend]}
              <span className="dhd-sr-only">{t(TREND_WORDS[trend])}</span>
            </span>
          ) : null}
          {deltaText !== null ? (
            <span className="tabular-nums text-body">
              {deltaText}
              {deltaSuffix ? <span className="ml-1">{deltaSuffix}</span> : null}
            </span>
          ) : null}
          {deltaLabel ? <span className="text-muted">{deltaLabel}</span> : null}
        </div>
      ) : null}

      {hint ? <p className="mt-1 text-2xs text-muted">{hint}</p> : null}
    </>
  );

  return (
    <div {...rest} aria-busy={loading || undefined} className={cn('relative min-w-0', className)}>
      <motion.div
        initial={reduceMotion ? false : { opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={reduceMotion ? { duration: 0 } : { duration: DUR.base, ease: EASE.out }}
        className={cn(loading && 'invisible')}
      >
        {badge}
      </motion.div>

      {loading ? (
        // The invisible badge above is what reserves the height, so the bars
        // can only ever overlay the space the loaded badge already occupies.
        <span aria-hidden="true" className="absolute inset-0 flex flex-col">
          <span className={cn(SHIMMER_BAR, 'h-3 w-20')} />
          <span className={cn(SHIMMER_BAR, 'mt-2 h-8 w-28')} />
          {hasDeltaLine ? <span className={cn(SHIMMER_BAR, 'mt-1.5 h-3 w-24')} /> : null}
          {hint ? <span className={cn(SHIMMER_BAR, 'mt-1 h-3 w-32')} /> : null}
        </span>
      ) : null}
    </div>
  );
}
